import { Link } from "react-router-dom";
import { MapPin, Calendar, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";

export default function EventCard({ event, index = 0 }) {
  const { t } = useLang();
  const isComingSoon = event.availability === "Coming Soon";
  const showPrice = event.starting_price > 0;
  const isContainFit = event.image_fit === "contain";

  const getAvailabilityClass = () => {
    if (isComingSoon) return "badge-coming-soon";
    if (event.availability === "Limited") return "badge-limited";
    return "badge-available";
  };

  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: index * 0.1 }}>
      <Link to={isComingSoon ? "#" : `/event/${event.slug}`} data-testid={`event-card-${event.slug}`}
        className={`event-card block bg-[#141414] border border-[#262626] overflow-hidden group ${isComingSoon ? "cursor-default" : ""}`}>
        <div className={`relative h-56 md:h-64 overflow-hidden ${isContainFit ? 'bg-black' : ''}`}>
          <img src={event.image_url} alt={event.title} className={`event-card-image w-full h-full transition-transform duration-700 ${isContainFit ? 'object-contain p-8' : 'object-cover'}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
          <div className="absolute top-3 right-3">
            <span className={`event-tag px-2.5 py-1 border ${getAvailabilityClass()}`}>{event.availability}</span>
          </div>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {event.tags.map((tag) => <span key={tag} className="event-tag px-2 py-0.5 bg-white/5 text-white/50 border border-white/10">{tag}</span>)}
          </div>
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-white mb-1">{event.title}</h3>
          {event.subtitle && <p className="text-[#C5A059] text-sm font-['Outfit'] font-light mb-4">{event.subtitle}</p>}
          <div className="flex items-center gap-4 mb-4 text-white/50 text-sm font-['Outfit']">
            <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#C5A059]/70" />{event.date}</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#C5A059]/70" />{event.location}</span>
          </div>
          <p className="text-white/40 text-sm font-['Outfit'] font-light leading-relaxed mb-6 line-clamp-2">{event.description}</p>
          <div className="flex items-center justify-between">
            {showPrice ? (
              <div>
                <span className="text-xs text-white/40 font-['Outfit'] uppercase tracking-wider">{t("from_label")}</span>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xl font-['Outfit'] font-semibold text-white">{event.starting_price.toLocaleString()}</span>
                  <span className="text-sm text-white/50 font-['Outfit']">{event.currency}</span>
                </div>
              </div>
            ) : (
              <span className="text-sm text-white/40 font-['Outfit'] italic">{t("pricing_soon")}</span>
            )}
            {!isComingSoon && (
              <span className="flex items-center gap-2 text-[#C5A059] text-xs tracking-[0.15em] uppercase font-['Outfit'] font-medium group-hover:gap-3 transition-all">
                {t("view_label")} <ArrowRight size={14} />
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
